import { createRootRoute, createRoute, createRouter, lazyRouteComponent } from '@tanstack/react-router';
import { CallbackPage } from '@/pages/callback';
import { NotFound } from '@/pages/not-found';
import { RootLayout } from '@/pages/root-layout';

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/home'), 'HomePage'),
});

// --- Public routes ---

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('@/pages/login'), 'LoginPage'),
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: CallbackPage,
});

// --- Org (tenant submission portal — public) ---

const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug',
  component: lazyRouteComponent(() => import('@/pages/org/org-layout'), 'OrgLayout'),
});

const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/org/org-landing'), 'OrgLanding'),
});

// --- Submitter routes (under /org/$slug, tenant-branded) ---

const submitterAuthCallbackRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/auth/callback',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/callback-page'), 'SubmitterCallbackPage'),
});

const submitterSubmissionRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/submission/$id',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/submitter-layout'), 'SubmitterLayout'),
});

const submitterUploadRoute = createRoute({
  getParentRoute: () => submitterSubmissionRoute,
  path: '/upload',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/upload-page'), 'UploadPage'),
});

const submitterFormRoute = createRoute({
  getParentRoute: () => submitterSubmissionRoute,
  path: '/form',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/form-page'), 'FormPage'),
});

const submitterProcessingRoute = createRoute({
  getParentRoute: () => submitterSubmissionRoute,
  path: '/processing',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/processing-page'), 'ProcessingPage'),
});

const submitterReviewRoute = createRoute({
  getParentRoute: () => submitterSubmissionRoute,
  path: '/review',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/review-page'), 'ReviewPage'),
});

const submitterConfirmRoute = createRoute({
  getParentRoute: () => submitterSubmissionRoute,
  path: '/confirm',
  component: lazyRouteComponent(() => import('@/pages/org/submitter/confirm-page'), 'ConfirmPage'),
});

// --- Platform Admin (requires platform_admin role) ---

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: lazyRouteComponent(() => import('@/pages/admin/admin-layout'), 'AdminLayout'),
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/admin/tenancy-overview'), 'TenancyOverview'),
});

const adminOnboardingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/onboard/$orgId',
  component: lazyRouteComponent(() => import('@/pages/admin/org-setup-dashboard'), 'OrgSetupDashboard'),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('@/pages/admin/system-audit'), 'SystemAudit'),
});

const adminAiConfigRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/ai-config',
  component: lazyRouteComponent(() => import('@/pages/admin/ai-config'), 'AiConfig'),
});

const adminAiQualityRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/ai-quality',
  component: lazyRouteComponent(() => import('@/pages/admin/ai-quality'), 'AiQuality'),
});

const adminCredentialsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/credentials',
  component: lazyRouteComponent(() => import('@/pages/admin/credential-health'), 'CredentialHealth'),
});

const adminThemeRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/theme',
  component: lazyRouteComponent(() => import('@/pages/admin/theme-browser'), 'ThemeBrowser'),
});

// --- Tenant Admin (org detail with tabs — requires curator_admin) ---

const tenantAdminRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId',
  component: lazyRouteComponent(() => import('@/pages/tenant-admin/tenant-admin-layout'), 'TenantAdminLayout'),
});

const tenantOverviewRoute = createRoute({
  getParentRoute: () => tenantAdminRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/tenant-admin/org-index-redirect'), 'OrgIndexRedirect'),
});

const tenantPreScreeningRoute = createRoute({
  getParentRoute: () => tenantAdminRoute,
  path: '/pre-screening',
  component: lazyRouteComponent(() => import('@/pages/tenant-admin/pre-screening'), 'PreScreening'),
});

const tenantInitialDdRoute = createRoute({
  getParentRoute: () => tenantAdminRoute,
  path: '/initial-dd',
  component: lazyRouteComponent(() => import('@/pages/tenant-admin/initial-dd'), 'InitialDd'),
});

const tenantAuditRoute = createRoute({
  getParentRoute: () => tenantAdminRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('@/pages/tenant-admin/tenant-audit'), 'TenantAudit'),
});

const curatorInboxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator',
  component: lazyRouteComponent(() => import('@/pages/curator/command-center'), 'CommandCenter'),
});

// Classic dashboard, parked until command-center cutover (SavaP-a1py)
const curatorClassicRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/classic',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-home'), 'CuratorHome'),
});

const curatorKanbanRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/kanban',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-dashboard'), 'CuratorDashboard'),
});

const curatorPipelineRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/pipeline',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-pipeline'), 'CuratorPipeline'),
});

const curatorFunnelRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/funnel',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-funnel-page'), 'CuratorFunnelPage'),
});

const curatorAnalyticsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/analytics',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-analytics-page'), 'CuratorAnalyticsPage'),
});

const curatorSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/settings',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-settings-redirect'), 'CuratorSettingsRedirect'),
});

// --- Curator Submission Detail (sibling of tenantAdminRoute — own layout) ---

const submissionDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/org/$orgId/curator/$submissionId',
  component: lazyRouteComponent(() => import('@/pages/curator/submission-detail-layout'), 'SubmissionDetailLayout'),
});

const submissionOverviewRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/curator/curator-overview'), 'CuratorOverview'),
});

const submissionPeopleRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/people',
  component: lazyRouteComponent(() => import('@/pages/curator/people-tab'), 'PeopleTab'),
});

const submissionPreScreeningRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/pre-screening',
  component: lazyRouteComponent(() => import('@/pages/curator/stage-evaluation'), 'PreScreeningEvaluation'),
});

const submissionPreScreeningScoreRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/pre-screening/score',
  component: lazyRouteComponent(() => import('@/pages/curator/stage-scoring-workspace'), 'PreScreeningWorkspace'),
});

const submissionInitialDdRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/initial-dd',
  component: lazyRouteComponent(() => import('@/pages/curator/stage-evaluation'), 'InitialDdEvaluation'),
});

const submissionInitialDdScoreRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/initial-dd/score',
  component: lazyRouteComponent(() => import('@/pages/curator/stage-scoring-workspace'), 'InitialDdWorkspace'),
});

const submissionDashboardRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('@/pages/curator/dashboard-tab'), 'DashboardTab'),
});

const submissionScoringRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/scoring',
  component: lazyRouteComponent(() => import('@/pages/curator/scoring-tab'), 'ScoringTab'),
});

const submissionClaimsRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/claims',
  component: lazyRouteComponent(() => import('@/pages/curator/claims-tab'), 'ClaimsTab'),
});

const submissionFitRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/fit',
  component: lazyRouteComponent(() => import('@/pages/curator/fit-tab'), 'FitTab'),
});

const submissionCommsRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/comms',
  component: lazyRouteComponent(() => import('@/pages/curator/comms-tab'), 'CommsTab'),
});

const submissionDispositionRoute = createRoute({
  getParentRoute: () => submissionDetailRoute,
  path: '/disposition',
  component: lazyRouteComponent(() => import('@/pages/curator/promotion/disposition-tab'), 'DispositionTab'),
});

// --- Route tree ---

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  callbackRoute,
  orgRoute.addChildren([
    orgIndexRoute,
    submitterAuthCallbackRoute,
    submitterSubmissionRoute.addChildren([
      submitterUploadRoute,
      submitterProcessingRoute,
      submitterFormRoute,
      submitterReviewRoute,
      submitterConfirmRoute,
    ]),
  ]),
  adminRoute.addChildren([
    adminIndexRoute,
    adminOnboardingRoute,
    adminAuditRoute,
    adminAiConfigRoute,
    adminAiQualityRoute,
    adminCredentialsRoute,
    adminThemeRoute,
    tenantAdminRoute.addChildren([tenantOverviewRoute, tenantPreScreeningRoute, tenantInitialDdRoute, tenantAuditRoute]),
    curatorInboxRoute,
    curatorClassicRoute,
    curatorKanbanRoute,
    curatorPipelineRoute,
    curatorFunnelRoute,
    curatorAnalyticsRoute,
    curatorSettingsRoute,
    submissionDetailRoute.addChildren([
      submissionOverviewRoute,
      submissionPeopleRoute,
      submissionPreScreeningRoute,
      submissionPreScreeningScoreRoute,
      submissionInitialDdRoute,
      submissionInitialDdScoreRoute,
      submissionDashboardRoute,
      submissionScoringRoute,
      submissionClaimsRoute,
      submissionFitRoute,
      submissionCommsRoute,
      submissionDispositionRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
